/**
 * Collection Page 2.0 — product grid decorator
 *
 * Iki isi yapar:
 *  1. Grid icine content tile (banner) <li>'lerini yerlestirir. Tile markup'i sunucu tarafinda
 *     grid'in icine <template data-content-tile-template> olarak basilir, yerlesim konumu
 *     urun sayisina bagli oldugu icin DOM'a buradan tasinir.
 *  2. Tile'in dusmedigi slotlarda urun kartini 2x2 buyutur (featured mozaik).
 *
 * Spec: koleksiyonda 3 veya daha az urun varsa mozaik/tile uygulanmaz, standart grid kalir.
 *
 * Mevcut load-more (dynamic-load-more.js) ve facets (facets.js) akislarina dokunmadan
 * calisir: grid degistiginde MutationObserver ile tekrar tetiklenir.
 */
(function () {
  if (window.collectionGridDecorator) return;

  // 3 veya daha az urun -> standart grid (dokuman: "Collection Page — Product Grid Layout")
  const MIN_PRODUCTS_FOR_FEATURED = 4;
  const DESKTOP_CYCLE = 16;
  const MOBILE_CYCLE = 5;

  const FEATURED_CLASSES = [
    'plp-product-item--featured-desktop',
    'plp-product-item--featured-mobile',
    'plp-product-item--featured-end',
    'plp-product-item--featured-start',
    'plp-desktop-only',
    'plp-mobile-only',
  ];

  class CollectionGridDecorator {
    constructor() {
      this.templateNodes = [];
      this.contentTileTemplates = [];
      this.observer = null;
      this.frame = null;
      this.lastGrid = null;
      this.columnsDesktop = 4;
      this.columnsMobile = 2;
      this.pageWidth = 1400;
    }

    /**
     * Grid olculerini <ul> uzerindeki data attribute'lardan okur.
     * Buyutulen kartlarin dogru cozunurlukte gorsel secmesi icin gerekli.
     */
    readGridConfig(grid) {
      this.columnsDesktop = Number(grid.dataset.columnsDesktop) || this.columnsDesktop;
      this.columnsMobile = Number(grid.dataset.columnsMobile) || this.columnsMobile;
      this.pageWidth = Number(grid.dataset.pageWidth) || this.pageWidth;
    }

    buildSizes(desktopSpan, mobileSpan) {
      const fixedWidth = Math.round(((this.pageWidth - 130) / this.columnsDesktop) * desktopSpan);
      return (
        `(min-width: ${this.pageWidth}px) ${fixedWidth}px,` +
        ` (min-width: 990px) calc((100vw - 130px) / ${this.columnsDesktop} * ${desktopSpan}),` +
        ` calc((100vw - 35px) / ${this.columnsMobile} * ${mobileSpan})`
      );
    }

    /**
     * Kart 2x2 buyudugunde tarayici hala "1/4 kolon" genisligine gore srcset'ten
     * kucuk gorseli seciyor ve gorsel bulanik kaliyor. Buyuyen kartlarda sizes
     * degeri gercek alana gore guncellenir, kart normale donunce geri alinir.
     */
    updateImageSizes(product) {
      const isFeaturedDesktop = product.classList.contains('plp-product-item--featured-desktop');
      const isFeaturedMobile = product.classList.contains('plp-product-item--featured-mobile');
      const images = product.querySelectorAll('.card__inner .card__media img[srcset]');

      images.forEach((image) => {
        if (image.dataset.plpBaseSizes === undefined) {
          image.dataset.plpBaseSizes = image.getAttribute('sizes') || '';
        }

        if (!isFeaturedDesktop && !isFeaturedMobile) {
          if (image.dataset.plpBaseSizes) image.setAttribute('sizes', image.dataset.plpBaseSizes);
          return;
        }

        image.setAttribute(
          'sizes',
          this.buildSizes(isFeaturedDesktop ? 2 : 1, isFeaturedMobile ? this.columnsMobile : 1)
        );
      });
    }

    getContainer() {
      return document.getElementById('ProductGridContainer');
    }

    getGrid() {
      const element = document.getElementById('product-grid');
      if (!element) return null;
      return element.tagName === 'UL' ? element : element.querySelector('ul');
    }

    /**
     * Grid icindeki tile template'lerini toplar. Load more sonrasi sonraki sayfanin
     * template'leri de eklendigi icin data-tile-index uzerinden tekillestirilir.
     */
    collectTemplates(grid) {
      const nodes = Array.from(grid.querySelectorAll(':scope > template[data-content-tile-template]'));
      const byIndex = new Map();

      nodes.forEach((node) => {
        const index = Number(node.dataset.tileIndex);
        if (!Number.isFinite(index) || index < 0) return;
        if (byIndex.has(index)) {
          node.remove();
          return;
        }
        byIndex.set(index, node);
      });

      this.templateNodes = Array.from(byIndex.keys())
        .sort((a, b) => a - b)
        .map((key) => byIndex.get(key));
      this.contentTileTemplates = this.templateNodes.map((node) => node.innerHTML);
    }

    createContentTile(index, type, position) {
      const content = this.contentTileTemplates[index];
      if (!content) return null;

      const tile = document.createElement('li');
      tile.className = [
        'grid__item',
        'plp-product-item',
        type === 'desktop' ? 'plp-product-item--featured-desktop' : 'plp-product-item--featured-mobile',
        type === 'desktop' && position === 'end' ? 'plp-product-item--featured-end' : '',
        type === 'desktop' && position === 'start' ? 'plp-product-item--featured-start' : '',
        'plp-content-tile',
        type === 'desktop' ? 'plp-desktop-only' : 'plp-mobile-only',
      ]
        .filter(Boolean)
        .join(' ');
      tile.innerHTML = content;

      return tile;
    }

    decorateGrid(grid) {
      // Onceki dekorasyonu birakip grid'i gorsel sirasiyla yeniden kurar.
      // Content tile, isgal ettigi slottaki urunden once eklenir.
      const products = Array.from(grid.querySelectorAll(':scope > li:not(.plp-content-tile)'));

      if (products.length < MIN_PRODUCTS_FOR_FEATURED) {
        products.forEach((product) => {
          product.classList.remove(...FEATURED_CLASSES);
          this.updateImageSizes(product);
        });
        grid.replaceChildren(...this.templateNodes, ...products);
        return;
      }

      const decoratedItems = [];
      let desktopSlotCounter = 0;
      let desktopTileIndex = 0;
      let desktopSecondSlotConsumed = false;
      let mobileSlotCounter = 0;
      let mobileTileIndex = 0;

      products.forEach((product, position) => {
        const remaining = products.length - position - 1;
        const desktopCyclePosition = position % DESKTOP_CYCLE;
        const mobileCyclePosition = position % MOBILE_CYCLE;

        product.classList.remove(...FEATURED_CLASSES);

        if (desktopCyclePosition === 0) {
          desktopSecondSlotConsumed = false;
        }

        if (desktopCyclePosition === 2 && remaining >= 1) {
          desktopSlotCounter += 1;

          // Desktop slot 1: pozisyon 2. Featured urun / content tile donusumlu gelir.
          // Content tile geldiginde satirin sonunda oturur.
          if (desktopSlotCounter % 2 === 0 && desktopTileIndex < this.contentTileTemplates.length) {
            decoratedItems.push(this.createContentTile(desktopTileIndex, 'desktop', 'end'));
            desktopTileIndex += 1;
          } else {
            product.classList.add('plp-product-item--featured-desktop', 'plp-product-item--featured-end');
          }
        }

        if (desktopCyclePosition === 8 && remaining >= 3) {
          const nextDesktopSlotCounter = desktopSlotCounter + 1;

          // Slot 2'deki content tile 2x2 sigabilsin diye pozisyon 8'de baslar.
          // Ayni dongudeki featured urun ise pozisyon 9'da baslar.
          if (nextDesktopSlotCounter % 2 === 0 && desktopTileIndex < this.contentTileTemplates.length) {
            desktopSlotCounter = nextDesktopSlotCounter;
            desktopSecondSlotConsumed = true;
            decoratedItems.push(this.createContentTile(desktopTileIndex, 'desktop', 'start'));
            desktopTileIndex += 1;
          }
        }

        if (desktopCyclePosition === 9 && !desktopSecondSlotConsumed && remaining >= 2) {
          const nextDesktopSlotCounter = desktopSlotCounter + 1;

          if (nextDesktopSlotCounter % 2 === 1 || desktopTileIndex >= this.contentTileTemplates.length) {
            desktopSlotCounter = nextDesktopSlotCounter;
            product.classList.add('plp-product-item--featured-desktop', 'plp-product-item--featured-start');
          }
        }

        if (mobileCyclePosition === 4 && remaining >= 1) {
          mobileSlotCounter += 1;

          // Mobilde 5 urunde bir kendi kadansi var ama ayni tile'lari kullanir,
          // boylece desktop ve mobil ayni icerigi one cikarir.
          if (mobileSlotCounter % 2 === 0 && mobileTileIndex < this.contentTileTemplates.length) {
            decoratedItems.push(this.createContentTile(mobileTileIndex, 'mobile'));
            mobileTileIndex += 1;
          } else {
            product.classList.add('plp-product-item--featured-mobile');
          }
        }

        this.updateImageSizes(product);
        decoratedItems.push(product);
      });

      // Template'ler grid'de kalir ki sonraki dekorasyonda tekrar okunabilsin.
      grid.replaceChildren(...this.templateNodes, ...decoratedItems.filter(Boolean));
    }

    decorate() {
      const grid = this.getGrid();
      if (!grid) return;

      this.stopObserving();
      try {
        this.readGridConfig(grid);
        this.collectTemplates(grid);
        this.decorateGrid(grid);
        this.lastGrid = grid;
      } catch (error) {
        console.error('[Collection grid decorator]', error);
      } finally {
        this.startObserving();
      }
    }

    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.decorate();
      });
    }

    startObserving() {
      const container = this.getContainer();
      if (!container || !window.MutationObserver) return;

      if (!this.observer) {
        this.observer = new MutationObserver((records) => this.handleMutations(records));
      }
      this.observer.observe(container, { childList: true, subtree: true });
    }

    stopObserving() {
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Yalnizca grid'in kendi cocuklari degistiginde ya da grid elemani komple
     * degistiginde tetiklenir; kart ici DOM gurultusune tepki vermez.
     */
    handleMutations(records) {
      const grid = this.getGrid();
      if (!grid) return;

      const gridReplaced = grid !== this.lastGrid;
      const gridChildrenChanged = records.some((record) => record.target === grid);

      if (!gridReplaced && !gridChildrenChanged) return;
      this.schedule();
    }

    init() {
      this.decorate();
    }
  }

  window.collectionGridDecorator = new CollectionGridDecorator();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.collectionGridDecorator.init(), { once: true });
  } else {
    window.collectionGridDecorator.init();
  }

  document.addEventListener('shopify:section:load', () => {
    window.collectionGridDecorator.lastGrid = null;
    window.collectionGridDecorator.decorate();
  });
})();
